import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of, throwError } from 'rxjs'
import { TaskStatus } from 'src/app/types/dto/tasks.dto'
import { environment } from 'src/environments/environment'
import {
  DEMO_ACTIVITIES,
  DEMO_CLAIMS,
  DEMO_CASHFLOW,
  DEMO_EXTRAS,
  DEMO_FORECAST_JOBS,
  DEMO_FORECAST_TASKS,
  DEMO_HOLIDAYS,
  DEMO_JOB_FIELDS,
  DEMO_JOB_LOOKUP,
  DEMO_JOBS,
  DEMO_TASK_MASTERS,
  DEMO_TASKS,
  DEMO_TRACKING_FIELDS,
  DEMO_USERS,
} from './demo-api.fixtures'

const REAL_API_ROUTES = [
  'applications/',
  'auth/',
  'companies/',
  'dash-boards/',
  'dash-cards/',
  'email-addresses/',
  'login-log',
  'users/',
]

@Injectable()
export class DemoApiInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isDemoUserList = req.method === 'GET' && this.getApiRoute(req.url) === 'companies/users'
    if (!this.isDemoRequest(req) || (this.isRealApiRoute(req.url) && !isDemoUserList)) {
      return next.handle(req)
    }

    if (req.method === 'GET' && this.getApiRoute(req.url) === 'job-task-forecasts/cashflow') {
      return of(new HttpResponse({ status: 200, body: DEMO_CASHFLOW, url: req.url }))
    }

    const response = this.getMockResponse(req)
    return response === undefined
      ? throwError(() => new HttpErrorResponse({
          status: 501,
          statusText: 'Demo API route not implemented',
          url: req.url,
        }))
      : of(new HttpResponse({ status: 200, body: response, url: req.url }))
  }

  private isDemoRequest(req: HttpRequest<unknown>): boolean {
    return environment.demoCompanyId != null
      && req.headers.get('company-id') === environment.demoCompanyId.toString()
  }

  private isRealApiRoute(url: string): boolean {
    const route = this.getApiRoute(url)
    return REAL_API_ROUTES.some((realRoute) => route.startsWith(realRoute))
  }

  private getMockResponse(req: HttpRequest<unknown>): unknown | undefined {
    const route = this.getApiRoute(req.url)

    if (req.method === 'GET' && route === 'jobs/for-dashboard') return DEMO_JOBS
    if (req.method === 'GET' && route === 'jobs/dashboard-lookup') return DEMO_JOB_LOOKUP
    if (req.method === 'GET' && route === 'companies/users') return DEMO_USERS
    if (req.method === 'GET' && route === 'activities') return DEMO_ACTIVITIES
    if (req.method === 'GET' && route === 'holidays') return DEMO_HOLIDAYS
    if (req.method === 'GET' && route === 'task-masters') return DEMO_TASK_MASTERS
    if (req.method === 'GET' && route === 'task-masters/task-codes') {
      return [...new Set(DEMO_TASK_MASTERS.map((task) => task.taskCode))]
    }
    if (req.method === 'GET' && route.startsWith('job-tasks/task-master/')) {
      const taskMasterId = Number(route.slice('job-tasks/task-master/'.length))
      return DEMO_TASKS.filter((task) => task.taskMasterId === taskMasterId)
    }
    if (req.method === 'GET' && route.startsWith('job-tasks/task-code/')) {
      const taskCode = decodeURIComponent(route.slice('job-tasks/task-code/'.length))
      const taskMasterIds = new Set(DEMO_TASK_MASTERS.filter((task) => task.taskCode === taskCode).map((task) => task.id))
      return DEMO_TASKS.filter((task) => taskMasterIds.has(task.taskMasterId!))
    }
    if (req.method === 'GET' && route === 'income-invoices') return DEMO_CLAIMS
    if (req.method === 'GET' && route === 'reports/jobs-between-tasks') {
      const params = new URL(req.urlWithParams, globalThis.location?.origin ?? 'http://localhost')
        .searchParams
      const taskMasterIdDone = Number(params.get('taskMasterIdDone'))
      const taskMasterIdIncomplete = Number(params.get('taskMasterIdIncomplete'))
      const completedJobIds = new Set(
        DEMO_TASKS
          .filter((task) =>
            task.taskMasterId === taskMasterIdDone && task.statusId === TaskStatus.Completed
          )
          .map((task) => task.jobId)
      )
      const incompleteTaskCompletedJobIds = new Set(
        DEMO_TASKS
          .filter((task) =>
            task.taskMasterId === taskMasterIdIncomplete &&
            task.statusId === TaskStatus.Completed
          )
          .map((task) => task.jobId)
      )

      return DEMO_JOBS
        .filter((job) =>
          job.isActive &&
          completedJobIds.has(job.id) &&
          !incompleteTaskCompletedJobIds.has(job.id)
        )
        .map((job) => job.jobNumber)
    }
    if (req.method === 'PATCH' && route === 'job-task-forecasts/recalc-completion') {
      return DEMO_FORECAST_TASKS
    }
    if (req.method === 'GET' && route === 'job-task-forecasts/forecast-job-data') {
      return DEMO_FORECAST_JOBS
    }
    if (req.method === 'GET' && route === 'order-line-extras/') return DEMO_EXTRAS
    if (req.method === 'GET' && route === 'tracking-fields-for-charts') {
      return DEMO_TRACKING_FIELDS
    }
    if (req.method === 'GET' && route === 'job-fields-for-charts') {
      const dataField = new URL(req.urlWithParams, globalThis.location?.origin ?? 'http://localhost').searchParams.get('dataField')
      return DEMO_JOB_FIELDS.filter((field) => field.dataField === dataField)
    }

    return undefined
  }

  private getApiRoute(url: string): string {
    const origin = globalThis.location?.origin ?? 'http://localhost'
    const apiPath = new URL(environment.apiUrl, origin).pathname
    const requestPath = new URL(url, origin).pathname
    return requestPath.startsWith(apiPath) ? requestPath.slice(apiPath.length) : requestPath
  }
}